import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  ShoppingCart, ArrowLeft, ArrowRight, Check, Loader2, AlertCircle,
  Plus, Minus, CheckCircle, FileText, Users
} from 'lucide-react';
import subscriptionService from '../../services/subscription';
import type {
  SubscriptionPlan, OrganisationSubscription,
  BatchPurchaseItem, BatchPurchaseResponse
} from '../../types/subscription';
import { useAuth } from '../../context/AuthContext';
import { isOrgAdmin } from '../../utils/auth';

// ============================================================================
// Types
// ============================================================================

interface CartItem {
  planId: number;
  plan: SubscriptionPlan;
  seats: Record<string, number>; // type_key → total_seats
}

type Step = 'configure' | 'review' | 'success';

const hasSeats = (item: CartItem) => Object.values(item.seats).some(v => v > 0);

// ============================================================================
// Component
// ============================================================================

/**
 * Add modules. Seats on a module the org already has are bought from that
 * module's Seats & Users page, where they get assigned — this page used to
 * do both, which left admins buying seats in one place and handing them out
 * in another.
 *
 * `?plan=<id>` preselects a module (the Activate / Reactivate buttons on the
 * subscriptions page). A cancelled module is bought here like a new one; the
 * server reactivates the existing row, and — unlike the old reactivate modal —
 * invoices it.
 *
 * How the invoice is produced is deliberately invisible here. Whether it went
 * out, failed, or is waiting on the accounting connection is the billing
 * team's concern, surfaced on their screens; the customer is told an invoice
 * will be emailed.
 */
function PurchaseFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = isOrgAdmin(user);

  const [step, setStep] = useState<Step>('configure');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<OrganisationSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [paymentReference, setPaymentReference] = useState('');

  const [result, setResult] = useState<BatchPurchaseResponse | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/subscriptions', { replace: true });
      return;
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptySeats = (plan: SubscriptionPlan) => {
    const seats: Record<string, number> = {};
    for (const lt of plan.license_types ?? []) seats[lt.type_key] = 0;
    // One seat on the first tier, so a preselected module is ready to review.
    const first = plan.license_types?.[0];
    if (first) seats[first.type_key] = 1;
    return seats;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, subsRes] = await Promise.all([
        subscriptionService.getAvailablePlans(),
        subscriptionService.getOrganisationSubscriptions(),
      ]);
      if (plansRes.success) setPlans(plansRes.data);
      if (subsRes.success) setSubscriptions(subsRes.data);

      const preselect = parseInt(searchParams.get('plan') || '', 10);
      const plan = plansRes.success ? plansRes.data.find(p => p.id === preselect) : undefined;
      const alreadyActive = subsRes.success && subsRes.data.some(s => s.plan_id === preselect && s.status === 'active');
      if (plan && !alreadyActive) {
        setCart([{ planId: plan.id, plan, seats: emptySeats(plan) }]);
      }
    } catch {
      setError('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  // Plans the org doesn't currently have active (cancelled ones included).
  const availablePlans = useMemo(() => {
    const activeModuleKeys = new Set(
      subscriptions.filter(s => s.status === 'active').map(s => s.module_key)
    );
    return plans.filter(p => !activeModuleKeys.has(p.module_key));
  }, [plans, subscriptions]);

  const wasCancelled = (planId: number) =>
    subscriptions.some(s => s.plan_id === planId && s.status === 'cancelled');

  // ============================================================================
  // Cart helpers
  // ============================================================================

  const cartIndexOf = (planId: number) => cart.findIndex(c => c.planId === planId);

  const togglePlanInCart = (plan: SubscriptionPlan) => {
    if (cartIndexOf(plan.id) >= 0) {
      setCart(prev => prev.filter(c => c.planId !== plan.id));
    } else {
      setCart(prev => [...prev, { planId: plan.id, plan, seats: emptySeats(plan) }]);
    }
  };

  const updateSeatCount = (cartIndex: number, typeKey: string, value: number) => {
    setCart(prev => prev.map((item, i) =>
      i === cartIndex ? { ...item, seats: { ...item.seats, [typeKey]: Math.max(0, value) } } : item
    ));
  };

  // ============================================================================
  // Cost calculation
  // ============================================================================

  const calculateItemCost = (item: CartItem): number => {
    let cost = Number(item.plan.base_price || 0);
    for (const lt of item.plan.license_types ?? []) {
      cost += (item.seats[lt.type_key] || 0) * Number(lt.price_per_seat || 0);
    }
    return cost;
  };

  const readyItems = cart.filter(hasSeats);

  const monthlyTotal = useMemo(
    () => cart.filter(hasSeats).reduce((sum, item) => sum + calculateItemCost(item), 0),
    [cart]
  );

  const invoiceTotal = useMemo(() => {
    const multiplier = billingCycle === 'annual' ? 12 : 1;
    const discount = billingCycle === 'annual' ? 10 : 0; // TODO: read from plan
    return monthlyTotal * multiplier * (1 - discount / 100);
  }, [monthlyTotal, billingCycle]);

  // ============================================================================
  // Submission
  // ============================================================================

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const items: BatchPurchaseItem[] = readyItems.map(item => ({
        type: 'new' as const,
        plan_id: item.planId,
        seats: Object.entries(item.seats)
          .filter(([, v]) => v > 0)
          .map(([type_key, total_seats]) => ({ type_key, total_seats })),
      }));

      if (items.length === 0) {
        setError('Choose at least one seat');
        return;
      }

      const res = await subscriptionService.batchPurchase({
        items,
        billing_cycle: billingCycle,
        payment_reference: paymentReference || undefined,
      });

      if (res.success) {
        setResult(res.data);
        setStep('success');
      } else {
        setError('Purchase failed');
      }
    } catch (err: any) {
      // The axios interceptor flattens errors onto err.message.
      setError(err?.message || err?.response?.data?.error?.message || 'Purchase failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Add Modules"
          subtitle="Choose modules and seats"
          icon={<ShoppingCart className="h-6 w-6 text-white" />}
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  const firstNewSubId = result?.subscriptions.find(s => s.id)?.id;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Add Modules"
        subtitle={
          step === 'configure' ? 'Choose modules and seats' :
          step === 'review' ? 'Check your order' :
          'Purchase complete'
        }
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {(['configure', 'review', 'success'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s ? 'bg-primary-600 text-white' :
                (['configure', 'review', 'success'].indexOf(step) > i) ? 'bg-primary-100 text-primary-700' :
                'bg-neutral-200 text-neutral-500'
              }`}>
                {(['configure', 'review', 'success'].indexOf(step) > i) ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm font-medium hidden sm:inline ${
                step === s ? 'text-primary-700' : 'text-neutral-500'
              }`}>
                {s === 'configure' ? 'Choose' : s === 'review' ? 'Review' : 'Done'}
              </span>
              {i < 2 && <div className="w-12 h-px bg-neutral-300" />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-error-600 flex-shrink-0" />
            <p className="text-error-700 min-w-0 break-words">{error}</p>
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 1: Choose */}
        {/* ================================================================ */}
        {step === 'configure' && (
          <div className="space-y-8">
            {availablePlans.length > 0 ? (
              <div>
                <p className="text-sm text-neutral-600 mb-4">
                  Need more seats on a module you already have? Add them from that module's
                  {' '}<button className="text-primary-700 underline" onClick={() => navigate('/subscriptions')}>Seats &amp; Users</button> page.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availablePlans.map(plan => {
                    const cartIndex = cartIndexOf(plan.id);
                    const cartItem = cartIndex >= 0 ? cart[cartIndex] : undefined;

                    return (
                      <div key={plan.id} className={`bg-white rounded-xl border-2 p-5 transition-all ${
                        cartItem ? 'border-primary-400 shadow-md' : 'border-neutral-200'
                      }`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-neutral-900">{plan.name}</h3>
                            {wasCancelled(plan.id) && (
                              <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">Previously cancelled</span>
                            )}
                            <p className="text-sm text-neutral-500">{plan.description}</p>
                          </div>
                          <button
                            onClick={() => togglePlanInCart(plan)}
                            aria-label={cartItem ? `Remove ${plan.name}` : `Add ${plan.name}`}
                            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                              cartItem ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                            }`}
                          >
                            {cartItem ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                          </button>
                        </div>

                        <div className="text-sm text-neutral-600 mb-3">
                          Base: {subscriptionService.formatCurrency(plan.base_price)}/mo
                        </div>

                        {cartItem && (plan.license_types ?? []).length > 0 && (
                          <div className="space-y-2 pt-3 border-t border-neutral-100">
                            {(plan.license_types ?? []).map(lt => (
                              <div key={lt.id} className="flex items-center justify-between gap-2">
                                <div className="text-sm min-w-0">
                                  <span className="font-medium text-neutral-700">{lt.name}</span>
                                  <span className="text-neutral-400 ml-1">
                                    ({subscriptionService.formatCurrency(Number(lt.price_per_seat))}/seat)
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => updateSeatCount(cartIndex, lt.type_key, (cartItem.seats[lt.type_key] || 0) - 1)}
                                    className="p-1 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-600"
                                    aria-label={`One fewer ${lt.name} seat`}
                                  >
                                    <Minus className="h-3.5 w-3.5" />
                                  </button>
                                  <input
                                    type="number"
                                    min={0}
                                    value={cartItem.seats[lt.type_key] || 0}
                                    onChange={e => updateSeatCount(cartIndex, lt.type_key, parseInt(e.target.value) || 0)}
                                    className="w-14 text-center border border-neutral-300 rounded px-1 py-0.5 text-sm"
                                  />
                                  <button
                                    onClick={() => updateSeatCount(cartIndex, lt.type_key, (cartItem.seats[lt.type_key] || 0) + 1)}
                                    className="p-1 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-600"
                                    aria-label={`One more ${lt.name} seat`}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
                <CheckCircle className="h-12 w-12 text-success-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-1">You have every module</h3>
                <p className="text-neutral-500">
                  Add seats from each module's
                  {' '}<button className="text-primary-700 underline" onClick={() => navigate('/subscriptions')}>Seats &amp; Users</button> page.
                </p>
              </div>
            )}

            {cart.length > 0 && (
              <div className="bg-white rounded-xl border border-neutral-200 p-6 sticky bottom-4 shadow-lg">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-sm text-neutral-500">Monthly total</div>
                    <div className="text-2xl font-bold text-neutral-900">
                      {subscriptionService.formatCurrency(monthlyTotal)}
                      <span className="text-sm text-neutral-500 font-normal">/mo</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => navigate('/subscriptions')}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      disabled={readyItems.length === 0}
                      onClick={() => { setError(null); setStep('review'); }}
                    >
                      Review
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 2: Review */}
        {/* ================================================================ */}
        {step === 'review' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <div className="p-6 border-b border-neutral-200">
                <h2 className="text-xl font-bold text-neutral-900">Your order</h2>
              </div>

              <div className="divide-y divide-neutral-100">
                {readyItems.map(item => (
                  <div key={item.planId} className="p-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-neutral-900 min-w-0">{item.plan.name}</h3>
                      <span className="font-bold text-neutral-900 whitespace-nowrap">
                        {subscriptionService.formatCurrency(calculateItemCost(item))}/mo
                      </span>
                    </div>

                    <div className="space-y-1 text-sm">
                      {Number(item.plan.base_price) > 0 && (
                        <div className="flex justify-between gap-3 text-neutral-600">
                          <span>Base subscription</span>
                          <span>{subscriptionService.formatCurrency(item.plan.base_price)}/mo</span>
                        </div>
                      )}
                      {(item.plan.license_types ?? []).map(lt => {
                        const seats = item.seats[lt.type_key] || 0;
                        if (seats <= 0) return null;
                        return (
                          <div key={lt.id} className="flex justify-between gap-3 text-neutral-600">
                            <span>{lt.name} ({seats} seat{seats > 1 ? 's' : ''})</span>
                            <span>{subscriptionService.formatCurrency(seats * Number(lt.price_per_seat))}/mo</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 border-t border-neutral-200">
                <label className="block text-sm font-medium text-neutral-700 mb-2">Pay</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      billingCycle === 'monthly'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      billingCycle === 'annual'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    Annually
                    <span className="ml-1 text-success-600 text-xs font-semibold">Save 10%</span>
                  </button>
                </div>
              </div>

              <div className="p-6 border-t border-neutral-200">
                <label htmlFor="purchase-po" className="block text-sm font-medium text-neutral-700 mb-1">
                  Your PO number (optional)
                </label>
                <input
                  id="purchase-po"
                  type="text"
                  value={paymentReference}
                  onChange={e => setPaymentReference(e.target.value)}
                  placeholder="Shown on the invoice"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="p-6 border-t border-neutral-200 bg-neutral-50">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-neutral-500">
                      {billingCycle === 'annual' ? 'Total per year' : 'Total per month'}
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="text-xs text-success-600">10% annual discount applied</div>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-neutral-900">
                    {subscriptionService.formatCurrency(invoiceTotal)}
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Modules are available straight away. We'll email you an invoice.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep('configure')}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Purchase
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 3: Success */}
        {/* ================================================================ */}
        {step === 'success' && result && (
          <div className="max-w-lg mx-auto text-center space-y-6">
            <div className="bg-success-50 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-success-600" />
            </div>

            <div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">You're all set</h2>
              <p className="text-neutral-600">
                {result.invoice.invoice_number
                  ? `Invoice ${result.invoice.invoice_number} is on its way to your billing email.`
                  : "Your invoice will be emailed to you shortly."}
                {' '}Now give your seats to the people who need them.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 p-6 text-left">
              <div className="space-y-2">
                {result.subscriptions.map((sub, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-neutral-700">{sub.plan_name}</span>
                    {sub.id ? (
                      <button
                        className="text-primary-700 font-medium hover:underline whitespace-nowrap"
                        onClick={() => navigate(`/subscriptions/${sub.id}/licenses`)}
                      >
                        Assign seats →
                      </button>
                    ) : (
                      <CheckCircle className="h-4 w-4 text-success-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/subscriptions/invoices')}>
                <FileText className="h-4 w-4" />
                Invoices
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => navigate(firstNewSubId ? `/subscriptions/${firstNewSubId}/licenses` : '/subscriptions')}
              >
                <Users className="h-4 w-4" />
                Assign seats
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PurchaseFlow;
